// Backend · Alerts: the editor for a custom rule (new or existing).

import { useEffect, useId, useState } from 'react';
import { ResponsiveOverlay } from '@/components/responsive-overlay';
import { SearchableDropdown } from '@/components/dropdowns';
import { MultiSelect } from '@/components/multi-select';
import { RequiredField, useRequiredFeedback } from '@/components/required-field';
import { MutationErrorLine } from '@/components/error-panel';
import { Icons } from '@/components/primitives';
import { adminAlertById, adminAlertRules, adminAlerts } from '@/api/resources';
import { api } from '@/api/client';
import { useRegisteredMutation } from '@/api/mutation';
import { usePixels, useProductions } from '@/api/hooks';
import type {
  AlertCategory,
  AlertEditorDefaults,
  AlertMetric,
  AlertMetricCatalogEntry,
  AlertRule,
  AlertRuleUpsert,
  AlertScope,
  AlertSeverity,
  AlertTrigger,
  Comparator,
  MetricWindow,
  NotificationChannel,
  RuleExplainer,
} from '@/api/types';
import {
  activationHelpForRule,
  activationModeOf,
  activationOptionsForRule,
  campaignRunningCapability,
  type ActivationModeId,
  type ActivationRule,
} from '@/lib/alert-activation';
import {
  CAT_LABEL,
  SEVERITIES,
  comparatorChoices,
  dayWindowChoices,
  dayWindowValue,
  hourWindowChoices,
  isAlertCategory,
  isAlertSeverity,
  isComparator,
  isDayWindow,
  isLogDetector,
  unitLabel,
  type DayWindow,
  type LogDetector,
  type TriggerFields,
} from '@/lib/alert-labels';
import { numericFieldError } from '@/lib/alert-validation';
import { ActivationSelector } from './backend-alerts-activation';
import { DeleteWithConfirm, Field, MetricExplainerHint, Row2, invalidate } from './backend-alerts-parts';

/** A window's `n` when the arm carries one. */
function windowN(window: MetricWindow | undefined): number | undefined {
  return window && 'n' in window ? window.n : undefined;
}

export interface AlertRuleDrawerProps {
  rule: AlertRule | null;
  /** Accepted for parity with the protected drawer; the editor shows the catalog copy instead. */
  explainer?: RuleExplainer | null;
  channels: readonly NotificationChannel[];
  metricCatalog?: readonly AlertMetricCatalogEntry[];
  availableCategories?: readonly AlertCategory[];
  editorDefaults?: AlertEditorDefaults | null;
  onClose: () => void;
}

export function AlertRuleDrawer({ rule, channels, metricCatalog = [], availableCategories = [], editorDefaults, onClose }: AlertRuleDrawerProps) {
  const editing = !!rule;
  const fieldIdBase = useId();
  const fid = (name: string) => `${fieldIdBase}-${name}`;
  const t: TriggerFields = rule?.trigger || {};
  const ruleScope: { production_id?: string | null; pixel_id?: string | null; level: string } | undefined = rule?.scope;
  const backfillMetrics = metricCatalog.filter((entry) => entry.category === 'backfill_quality' && entry.available);
  const pixelMetrics = metricCatalog.filter((entry) => entry.category === 'pixel' && entry.available);
  const defaultCategory = availableCategories.includes('backfill_quality')
    ? 'backfill_quality'
    : (availableCategories.find((candidate) => candidate !== 'system') ?? 'system');
  const initialCategory = rule?.category ?? defaultCategory;
  const initialMetric = t.type === 'metric'
    ? metricCatalog.find((entry) => entry.metric === t.metric)
    : (initialCategory === 'pixel' ? pixelMetrics[0] : backfillMetrics[0]);
  const {
    messages: ruleRequiredMessages,
    attempt: ruleRequiredAttempt,
    report: reportRuleRequired,
    clear: clearRuleRequired,
  } = useRequiredFeedback();
  // Entity lists for the name-based scope pickers (never type a raw id).
  const { data: productions = [] } = useProductions();
  const pixelsQuery = usePixels();
  const pixels = pixelsQuery.data ?? [];
  const [name, setName] = useState(rule?.name ?? '');
  const [category, setCategory] = useState<AlertCategory>(initialCategory);
  const [severity, setSeverity] = useState<AlertSeverity>(rule?.severity ?? 'warn');
  const [cooldown, setCooldown] = useState<string | number>(rule?.cooldown_minutes ?? editorDefaults?.cooldown_minutes ?? '');
  const [autoResolve, setAutoResolve] = useState(rule?.auto_resolve ?? true);
  const [channelIds, setChannelIds] = useState<string[]>(rule?.channel_ids ?? []);
  const [activationMode, setActivationMode] = useState<ActivationModeId>(rule ? activationModeOf(rule) : 'always');
  // scope (production for backfill; pixel for pixel monitoring)
  const [allProductions, setAllProductions] = useState(!(ruleScope?.production_id));
  const [productionId, setProductionId] = useState(ruleScope?.production_id ?? '');
  const [allPixels, setAllPixels] = useState(!(ruleScope?.pixel_id));
  const [pixelId, setPixelId] = useState(ruleScope?.pixel_id ?? '');
  const [scopeChanged, setScopeChanged] = useState(false);
  // system trigger fields
  // `null` = nothing the editor can build: a saved trigger it has no detector
  // for (a job failure) must be re-chosen, never rewritten silently.
  const [detector, setDetector] = useState<LogDetector | null>(
    isLogDetector(t.type) ? t.type : !t.type || t.type === 'metric' ? 'error_spike' : null,
  );
  const [component, setComponent] = useState(t.component ?? '');
  const [minCount, setMinCount] = useState<string | number>(t.min_count ?? editorDefaults?.error_min_count ?? '');
  const [winMinutes, setWinMinutes] = useState<string | number>(t.window_minutes ?? editorDefaults?.error_window_minutes ?? '');
  const [silenceMin, setSilenceMin] = useState<string | number>(t.silence_minutes ?? editorDefaults?.silence_minutes ?? '');
  // metric trigger fields (shared by backfill + pixel; defaults depend on category)
  // The metric pickers hold `null` until something is chosen (an empty
  // catalog chooses nothing); the body is built only from chosen values.
  const [metric, setMetric] = useState<AlertMetric | null>(t.type === 'metric' ? t.metric ?? null : initialMetric?.metric ?? null);
  const [op, setOp] = useState<Comparator | null>(t.op ?? initialMetric?.default_op ?? null);
  const [threshold, setThreshold] = useState<string | number>(t.threshold ?? initialMetric?.default_threshold ?? '');
  // A backfill rule picks a day-granular window. A saved hour window, which
  // this picker cannot show, starts unchosen. A saved rolling length the
  // picker does not list (1 day, set through the API) is kept and offered as
  // its own option, never widened to the listed 7 days.
  const savedDayWindow = isDayWindow(t.window) ? t.window : null;
  const initialDayWindow = t.window
    ? savedDayWindow
    : (isDayWindow(initialMetric?.default_window) ? initialMetric.default_window : null);
  const [dayWindow, setDayWindow] = useState<DayWindow | null>(initialDayWindow);
  // The same for a pixel rule's hours and for either rule's condition: a saved
  // value the picker does not list shows as itself and is kept.
  const savedHours = t.window?.kind === 'rolling_hours' ? t.window.n : null;
  const [pixelHours, setPixelHours] = useState<number | null>(savedHours ?? windowN(initialMetric?.default_window) ?? null);
  const [minTickets, setMinTickets] = useState<string | number>(t.guards?.min_tickets ?? initialMetric?.default_min_tickets ?? '');

  const comingSoon = !availableCategories.includes(category);
  const activationScope: AlertScope = category === 'system'
    ? { level: 'global' }
    : category === 'pixel'
      ? { level: 'pixel', pixel_id: allPixels ? null : (pixelId || null) }
      : { level: 'production', production_id: allProductions ? null : (productionId || null) };
  const activationRule: ActivationRule = {
    ...(rule ?? {}),
    scope: activationScope,
    trigger: category === 'system' ? { type: detector ?? 'error_spike' } : { type: 'metric', metric: metric ?? undefined },
  };
  const activationCapability = campaignRunningCapability(
    activationRule,
    metricCatalog,
    pixels,
    pixelsQuery.data !== undefined,
  );
  const activationOptions = activationOptionsForRule(activationRule, metricCatalog, {
    currentMode: activationMode,
    pixels,
    pixelsLoaded: pixelsQuery.data !== undefined,
  });
  useEffect(() => {
    // Preserve an unsupported persisted mode on initial read. Once the operator
    // changes a scope or metric, move an invalid draft to the safe Always mode.
    if (!scopeChanged) return;
    // While the capability is still unknown (pixels loading), keep the flag
    // armed so the demotion re-runs when the answer arrives.
    if (activationCapability === 'unknown') return;
    if (activationCapability === 'unsupported' && activationMode === 'campaign_running') {
      setActivationMode('always');
    }
    setScopeChanged(false);
  }, [activationCapability, activationMode, scopeChanged]);

  const applyMetricDefaults = (entry: AlertMetricCatalogEntry | undefined) => {
    if (!entry) return;
    setScopeChanged(true);
    setMetric(entry.metric);
    setOp(entry.default_op);
    setThreshold(entry.default_threshold);
    setDayWindow(isDayWindow(entry.default_window) ? entry.default_window : null);
    if (entry.default_window.kind === 'rolling_hours') setPixelHours(entry.default_window.n);
    setMinTickets(entry.default_min_tickets);
    clearRuleRequired('alert-rule-threshold');
    clearRuleRequired('alert-rule-min-tickets');
  };

  // Switching category resets the metric picker to that category's defaults so
  // the shared `metric`/`op`/`threshold` state never holds a cross-category id.
  const onCategoryChange = (c: AlertCategory) => {
    setCategory(c);
    setScopeChanged(true);
    clearRuleRequired('alert-rule-scope');
    clearRuleRequired('alert-rule-threshold');
    clearRuleRequired('alert-rule-min-tickets');
    if (c === 'pixel') applyMetricDefaults(pixelMetrics[0]);
    else if (c === 'backfill_quality') applyMetricDefaults(backfillMetrics[0]);
  };

  const saveM = useRegisteredMutation({
    domains: ['configuration'],
    mutationFn: (body: AlertRuleUpsert) => rule
      ? api.patch(`/admin/alert-rules/${encodeURIComponent(rule.id)}`, { body })
      : api.post('/admin/alert-rules', { body }),
    onSuccess: (_data, body) => {
      invalidate(adminAlertRules.all);
      // Saving a rule as Off resolves its open incidents server-side.
      if (body?.activation_mode === 'off') invalidate(adminAlerts.all, adminAlertById.all);
      onClose();
    },
  });
  const deleteM = useRegisteredMutation({
    domains: ['configuration'],
    mutationFn: async (_: void) => {
      // Delete is offered only while editing a saved rule.
      if (!rule) throw new Error('There is no saved rule to delete.');
      return api.delete(`/admin/alert-rules/${encodeURIComponent(rule.id)}`);
    },
    onSuccess: () => { invalidate(adminAlertRules.all); onClose(); },
  });

  // Each picker lists its offered values plus the saved and the current one.
  const dayChoices = dayWindowChoices([savedDayWindow, dayWindow]);
  const hourChoices = hourWindowChoices([savedHours, pixelHours]);
  const opChoices = comparatorChoices([t.op, op]);

  const metricMeta = category === 'pixel'
    ? pixelMetrics.find((candidate) => candidate.metric === metric)
    : backfillMetrics.find((candidate) => candidate.metric === metric);
  // What still has to be chosen before a body can be built. The metric must
  // be one this category's picker lists, so a category switch with no metric
  // to offer never carries the previous category's metric along.
  const metricChoices = category === 'pixel' ? pixelMetrics : backfillMetrics;
  const missingChoice = category === 'system'
    ? (detector ? null : 'Choose a detector.')
    : !metricMeta
      ? (metricChoices.length ? 'Choose a metric.' : 'No metric is available for this category yet.')
      : !op
        ? 'Choose a condition.'
        : (category === 'pixel' ? pixelHours == null : !dayWindow)
          ? 'Choose a window.'
          : null;

  // Fields the editor shows no control for keep their saved values: a guard or
  // a signature set through the API is not wiped by the next save here.
  const savedTrigger = rule?.trigger;
  const savedGuards = savedTrigger?.type === 'metric' || savedTrigger?.type === 'system_metric' ? savedTrigger.guards : null;
  const savedSignature = savedTrigger?.type === 'error_spike' ? savedTrigger.signature : null;

  /** The request body, or `null` while a required choice is still unmade. */
  const buildBody = (): AlertRuleUpsert | null => {
    let scope: AlertScope;
    let trigger: AlertTrigger;
    if (category === 'system') {
      if (!detector) return null;
      scope = { level: 'global' };
      if (detector === 'error_spike') trigger = { type: 'error_spike', component: component || null, signature: savedSignature, min_count: Number(minCount), window_minutes: Number(winMinutes) };
      else if (detector === 'new_error') trigger = { type: 'new_error', component: component || null, window_minutes: Number(winMinutes) };
      else trigger = { type: 'silence', component: component || null, silence_minutes: Number(silenceMin) };
    } else {
      if (!metricMeta || !op) return null;
      const guards = { min_tickets: Number(minTickets), min_spend_cents: savedGuards?.min_spend_cents ?? null, min_impressions: savedGuards?.min_impressions ?? null };
      if (category === 'pixel') {
        if (pixelHours == null) return null;
        scope = { level: 'pixel', pixel_id: allPixels ? null : (pixelId.trim() || null) };
        trigger = { type: 'metric', metric: metricMeta.metric, op, threshold: Number(threshold), window: { kind: 'rolling_hours', n: pixelHours }, guards };
      } else {
        if (!dayWindow) return null;
        scope = { level: 'production', production_id: allProductions ? null : (productionId || null) };
        trigger = { type: 'metric', metric: metricMeta.metric, op, threshold: Number(threshold), window: dayWindow, guards };
      }
    }
    // A persisted campaign_running mode this shape no longer supports is
    // demoted at save time instead of posting a body the server rejects.
    // 'unknown' keeps the mode: the server's live pixel-mapping guard is the
    // authority there.
    const effectiveActivationMode =
      activationMode === 'campaign_running' && activationCapability === 'unsupported'
        ? 'always'
        : activationMode;
    return {
      name: name.trim(),
      enabled: effectiveActivationMode !== 'off',
      activation_mode: effectiveActivationMode,
      category,
      scope,
      trigger,
      severity,
      channel_ids: channelIds,
      cooldown_minutes: Number(cooldown),
      auto_resolve: autoResolve,
    };
  };

  const nameError = !name.trim() ? 'Enter a name for this alert rule.' : null;
  const scopeError = category === 'backfill_quality' && !allProductions && !productionId
    ? 'Select a production or enable All productions.'
    : category === 'pixel' && !allPixels && !pixelId.trim()
      ? 'Select a pixel or enable All active pixels.'
      : null;
  const minCountError = category === 'system' && detector === 'error_spike'
    ? numericFieldError(minCount, 'Minimum errors', { integer: true, min: 1 })
    : null;
  const windowError = category === 'system' && (detector === 'error_spike' || detector === 'new_error')
    ? numericFieldError(winMinutes, 'Window', { integer: true, min: 1 })
    : null;
  const silenceError = category === 'system' && detector === 'silence'
    ? numericFieldError(silenceMin, 'Silence window', { integer: true, min: 1 })
    : null;
  const thresholdError = category !== 'system' && !comingSoon
    ? numericFieldError(threshold, 'Threshold', {
        min: 0,
        max: metricMeta?.unit === 'pct' ? 100 : undefined,
      })
    : null;
  const needsTicketGuard = category === 'backfill_quality'
    || (category === 'pixel' && !!metricMeta?.rate_guard);
  const minTicketsError = needsTicketGuard
    ? numericFieldError(
        minTickets,
        category === 'pixel' ? 'Minimum purchases' : 'Minimum tickets',
        { integer: true, min: 1 },
      )
    : null;
  const cooldownError = !comingSoon
    ? numericFieldError(cooldown, 'Cooldown', { integer: true, min: 0 })
    : null;
  const ruleIssues = [
    nameError ? { key: 'alert-rule-name', message: nameError } : null,
    scopeError ? { key: 'alert-rule-scope', message: scopeError } : null,
    minCountError ? { key: 'alert-rule-min-count', message: minCountError } : null,
    windowError ? { key: 'alert-rule-window', message: windowError } : null,
    silenceError ? { key: 'alert-rule-silence', message: silenceError } : null,
    thresholdError ? { key: 'alert-rule-threshold', message: thresholdError } : null,
    minTicketsError ? { key: 'alert-rule-min-tickets', message: minTicketsError } : null,
    cooldownError ? { key: 'alert-rule-cooldown', message: cooldownError } : null,
  ].filter(Boolean);
  const canBuild = !comingSoon && missingChoice === null;
  const saveReasonId = useId();
  const attemptSaveRule = () => {
    if (saveM.isPending || !canBuild) return;
    if (!reportRuleRequired(ruleIssues)) return;
    const body = buildBody();
    if (!body) return;
    if (activationMode === 'campaign_running' && activationCapability === 'unsupported') {
      // Reflect the save-time demotion in the visible control too.
      setActivationMode('always');
    }
    saveM.mutate(body);
  };

  return (
    <>

      <ResponsiveOverlay label="backend alerts details" onClose={onClose} className="drawer" style={{ width: 560 }}>
        <div className="drawer-hd">
          <RequiredField
            fieldKey="alert-rule-name"
            message={ruleRequiredMessages['alert-rule-name']}
            attempt={ruleRequiredAttempt}
            style={{ flex: 1 }}
          >
            <div className="muted mono" style={{ fontSize: 10, letterSpacing: '.12em', textTransform: 'uppercase' }}>{editing ? 'Edit rule' : 'New rule'}</div>
            <input
              className="input"
              data-validation-focus
              style={{ fontSize: 18, fontWeight: 700, marginTop: 4, width: '100%' }}
              aria-label="Rule name"
              placeholder="Rule name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                clearRuleRequired('alert-rule-name');
              }}
            />
          </RequiredField>
          <button className="btn btn-sm btn-ghost" onClick={onClose} style={{ fontSize: 18, padding: '4px 10px' }}>×</button>
        </div>

        <div className="drawer-body">
          <label className="field-lbl" htmlFor={fid('category')}><span>Category</span></label>
          <select id={fid('category')} className="input" style={{ width: '100%', fontSize: 13, marginBottom: 12 }} value={category} onChange={(e) => { if (isAlertCategory(e.target.value)) onCategoryChange(e.target.value); }} disabled={editing}>
            {Object.entries(CAT_LABEL).map(([id, label]) => <option key={id} value={id}>{label}{!isAlertCategory(id) || !availableCategories.includes(id) ? ' — coming soon' : ''}</option>)}
          </select>

          {comingSoon && (
            <div className="panel" style={{ padding: 14, marginBottom: 12, fontSize: 12.5, color: 'var(--ink-2)' }}>
              Campaign pacing & ROAS rules are coming once ad-spend data is backfilled. The rule model already supports them — this editor unlocks when the data lands.
            </div>
          )}

          {category === 'system' && !comingSoon && (
            <>
              <label className="field-lbl" htmlFor={fid('detector')}><span>Detector</span></label>
              <select id={fid('detector')}
                className="input"
                style={{ width: '100%', fontSize: 13, marginBottom: 10 }}
                value={detector ?? ''}
                onChange={(e) => {
                  if (isLogDetector(e.target.value)) setDetector(e.target.value);
                  clearRuleRequired('alert-rule-min-count');
                  clearRuleRequired('alert-rule-window');
                  clearRuleRequired('alert-rule-silence');
                }}
              >
                {detector == null && <option value="" disabled>Choose a detector…</option>}
                <option value="error_spike">Error spike</option>
                <option value="new_error">New error signature</option>
                {/* Log-silence is no longer creatable — liveness is a positive
                    heartbeat signal, not inferred from log absence. The option
                    still renders when editing a pre-existing silence rule so its
                    value isn't silently dropped. */}
                {editing && detector === 'silence' && <option value="silence">Component silence</option>}
              </select>
              <Field controlId={fid('component')} label="Component filter (blank = all)"><input id={fid('component')} className="input mono" style={{ width: '100%', fontSize: 12 }} placeholder="api_worker" value={component} onChange={(e) => setComponent(e.target.value)} /></Field>
              {detector === 'error_spike' && (
                <Row2>
                  <Field controlId={fid('min-errors-2')}
                    label="Min errors"
                    fieldKey="alert-rule-min-count"
                    message={ruleRequiredMessages['alert-rule-min-count']}
                    attempt={ruleRequiredAttempt}
                  >
                    <input id={fid('min-errors-2')} type="number" className="input" style={{ width: '100%' }} value={minCount} onChange={(e) => {
                      setMinCount(e.target.value);
                      clearRuleRequired('alert-rule-min-count');
                    }} />
                  </Field>
                  <Field controlId={fid('window-min-3')}
                    label="Window (min)"
                    fieldKey="alert-rule-window"
                    message={ruleRequiredMessages['alert-rule-window']}
                    attempt={ruleRequiredAttempt}
                  >
                    <input id={fid('window-min-3')} type="number" className="input" style={{ width: '100%' }} value={winMinutes} onChange={(e) => {
                      setWinMinutes(e.target.value);
                      clearRuleRequired('alert-rule-window');
                    }} />
                  </Field>
                </Row2>
              )}
              {detector === 'new_error' && (
                <Field controlId={fid('window-min-4')}
                  label="Window (min)"
                  fieldKey="alert-rule-window"
                  message={ruleRequiredMessages['alert-rule-window']}
                  attempt={ruleRequiredAttempt}
                >
                  <input id={fid('window-min-4')} type="number" className="input" style={{ width: '100%' }} value={winMinutes} onChange={(e) => {
                    setWinMinutes(e.target.value);
                    clearRuleRequired('alert-rule-window');
                  }} />
                </Field>
              )}
              {detector === 'silence' && (
                <Field controlId={fid('silent-for-min-5')}
                  label="Silent for (min)"
                  fieldKey="alert-rule-silence"
                  message={ruleRequiredMessages['alert-rule-silence']}
                  attempt={ruleRequiredAttempt}
                >
                  <input id={fid('silent-for-min-5')} type="number" className="input" style={{ width: '100%' }} value={silenceMin} onChange={(e) => {
                    setSilenceMin(e.target.value);
                    clearRuleRequired('alert-rule-silence');
                  }} />
                </Field>
              )}
            </>
          )}

          {category === 'backfill_quality' && !comingSoon && (
            <>
              <label className="field-lbl"><span>Scope</span></label>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, marginBottom: 6 }}>
                <input type="checkbox" checked={allProductions} onChange={(e) => {
                  setAllProductions(e.target.checked);
                  setScopeChanged(true);
                  clearRuleRequired('alert-rule-scope');
                }} /> All productions
              </label>
              {!allProductions && (
                <Field
                  label="Production"
                  fieldKey="alert-rule-scope"
                  message={ruleRequiredMessages['alert-rule-scope']}
                  attempt={ruleRequiredAttempt}
                >
                  <SearchableDropdown ariaLabel="Production" value={productionId} onChange={(nextProductionId) => {
                    setProductionId(nextProductionId);
                    setScopeChanged(true);
                    clearRuleRequired('alert-rule-scope');
                  }}
                    options={productions.map((p) => ({ id: p.id, label: p.name }))}
                    placeholder="Select a production…" searchPlaceholder="Search productions…" popWidth={440} />
                </Field>
              )}
              <label className="field-lbl" htmlFor={fid('metric')}><span>Metric</span></label>
              <select id={fid('metric')} className="input" style={{ width: '100%', fontSize: 13, marginBottom: 4 }} value={metricMeta?.metric ?? ''} onChange={(e) => {
                applyMetricDefaults(backfillMetrics.find((entry) => entry.metric === e.target.value));
              }}>
                {!metricMeta && <option value="" disabled>Choose a metric…</option>}
                {backfillMetrics.map((m) => <option key={m.metric} value={m.metric}>{m.label}</option>)}
              </select>
              <MetricExplainerHint entry={backfillMetrics.find((entry) => entry.metric === metric)} />
              <Row2>
                <Field controlId={fid('condition-7')} label="Condition">
                  <select id={fid('condition-7')} className="input" style={{ width: '100%', fontSize: 13 }} value={op ?? ''} onChange={(e) => { if (isComparator(e.target.value)) setOp(e.target.value); }}>
                    {op == null && <option value="" disabled>Choose…</option>}
                    {opChoices.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                  </select>
                </Field>
                <Field controlId={fid('threshold-8')}
                  label={`Threshold (${unitLabel(metricMeta?.unit, 'value')})`}
                  fieldKey="alert-rule-threshold"
                  message={ruleRequiredMessages['alert-rule-threshold']}
                  attempt={ruleRequiredAttempt}
                >
                  <input id={fid('threshold-8')} type="number" className="input" style={{ width: '100%' }} value={threshold} onChange={(e) => {
                    setThreshold(e.target.value);
                    clearRuleRequired('alert-rule-threshold');
                  }} />
                </Field>
              </Row2>
              <Row2>
                <Field controlId={fid('window-9')} label="Window">
                  <select id={fid('window-9')} className="input" style={{ width: '100%', fontSize: 13 }} value={dayWindow ? dayWindowValue(dayWindow) : ''} onChange={(e) => {
                    const choice = dayChoices.find((candidate) => candidate.value === e.target.value);
                    if (choice) setDayWindow(choice.window);
                  }}>
                    {dayWindow == null && <option value="" disabled>Choose a window…</option>}
                    {dayChoices.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
                  </select>
                </Field>
                <Field controlId={fid('min-tickets-guard-10')}
                  label="Min tickets (guard)"
                  fieldKey="alert-rule-min-tickets"
                  message={ruleRequiredMessages['alert-rule-min-tickets']}
                  attempt={ruleRequiredAttempt}
                >
                  <input id={fid('min-tickets-guard-10')} type="number" className="input" style={{ width: '100%' }} value={minTickets} onChange={(e) => {
                    setMinTickets(e.target.value);
                    clearRuleRequired('alert-rule-min-tickets');
                  }} />
                </Field>
              </Row2>
            </>
          )}

          {category === 'pixel' && !comingSoon && (
            <>
              <label className="field-lbl"><span>Scope</span></label>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, marginBottom: 6 }}>
                <input type="checkbox" checked={allPixels} onChange={(e) => {
                  setAllPixels(e.target.checked);
                  setScopeChanged(true);
                  clearRuleRequired('alert-rule-scope');
                }} /> All active pixels
              </label>
              {!allPixels && (
                <Field
                  label="Pixel"
                  fieldKey="alert-rule-scope"
                  message={ruleRequiredMessages['alert-rule-scope']}
                  attempt={ruleRequiredAttempt}
                >
                  <SearchableDropdown ariaLabel="Pixel" value={pixelId} onChange={(nextPixelId) => {
                    setPixelId(nextPixelId);
                    setScopeChanged(true);
                    clearRuleRequired('alert-rule-scope');
                  }}
                    options={pixels.map((p) => ({ id: p.id, label: p.description || p.id }))}
                    placeholder="Select a pixel…" searchPlaceholder="Search pixels…" popWidth={440} />
                </Field>
              )}
              <label className="field-lbl" htmlFor={fid('signal')}><span>Signal</span></label>
              <select id={fid('signal')} className="input" style={{ width: '100%', fontSize: 13, marginBottom: 10 }} value={metricMeta?.metric ?? ''} onChange={(e) => applyMetricDefaults(pixelMetrics.find((entry) => entry.metric === e.target.value))}>
                {!metricMeta && <option value="" disabled>Choose a signal…</option>}
                {pixelMetrics.map((m) => <option key={m.metric} value={m.metric}>{m.label}</option>)}
              </select>
              <Row2>
                <Field controlId={fid('condition-12')} label="Condition">
                  <select id={fid('condition-12')} className="input" style={{ width: '100%', fontSize: 13 }} value={op ?? ''} onChange={(e) => { if (isComparator(e.target.value)) setOp(e.target.value); }}>
                    {op == null && <option value="" disabled>Choose…</option>}
                    {opChoices.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                  </select>
                </Field>
                <Field controlId={fid('threshold-13')}
                  label={`Threshold (${unitLabel(metricMeta?.unit, 'count')})`}
                  fieldKey="alert-rule-threshold"
                  message={ruleRequiredMessages['alert-rule-threshold']}
                  attempt={ruleRequiredAttempt}
                >
                  <input id={fid('threshold-13')} type="number" className="input" style={{ width: '100%' }} value={threshold} onChange={(e) => {
                    setThreshold(e.target.value);
                    clearRuleRequired('alert-rule-threshold');
                  }} />
                </Field>
              </Row2>
              <Row2>
                <Field controlId={fid('window-14')} label="Window">
                  <select id={fid('window-14')} className="input" style={{ width: '100%', fontSize: 13 }} value={pixelHours ?? ''} onChange={(e) => setPixelHours(Number(e.target.value))}>
                    {pixelHours == null && <option value="" disabled>Choose a window…</option>}
                    {hourChoices.map((w) => <option key={w.n} value={w.n}>{w.label}</option>)}
                  </select>
                </Field>
                {metricMeta?.rate_guard && (
                  <Field controlId={fid('min-purchases-guard-15')}
                    label="Min purchases (guard)"
                    fieldKey="alert-rule-min-tickets"
                    message={ruleRequiredMessages['alert-rule-min-tickets']}
                    attempt={ruleRequiredAttempt}
                  >
                    <input id={fid('min-purchases-guard-15')} type="number" className="input" style={{ width: '100%' }} value={minTickets} onChange={(e) => {
                      setMinTickets(e.target.value);
                      clearRuleRequired('alert-rule-min-tickets');
                    }} />
                  </Field>
                )}
              </Row2>
              <MetricExplainerHint entry={pixelMetrics.find((entry) => entry.metric === metric)} />
            </>
          )}

          {!comingSoon && (
            <>
              <Field label="Monitoring">
                <div style={{ display: 'grid', justifyItems: 'start', gap: 6 }}>
                  <ActivationSelector
                    value={activationMode}
                    options={activationOptions}
                    label="Monitoring mode"
                    onChange={setActivationMode}
                  />
                  <div className="field-hint" style={{ margin: 0 }}>
                    {activationHelpForRule(activationRule, metricCatalog, {
                      currentMode: activationMode,
                      pixels,
                      pixelsLoaded: pixelsQuery.data !== undefined,
                    })}
                  </div>
                </div>
              </Field>
              <div style={{ borderTop: '1px solid var(--line)', margin: '14px 0', paddingTop: 12 }} />
              <Row2>
                <Field controlId={fid('severity-16')} label="Severity">
                  <select id={fid('severity-16')} className="input" style={{ width: '100%', fontSize: 13 }} value={severity} onChange={(e) => { if (isAlertSeverity(e.target.value)) setSeverity(e.target.value); }}>
                    {SEVERITIES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                  </select>
                </Field>
                <Field controlId={fid('cooldown-min-17')}
                  label="Cooldown (min)"
                  fieldKey="alert-rule-cooldown"
                  message={ruleRequiredMessages['alert-rule-cooldown']}
                  attempt={ruleRequiredAttempt}
                >
                  <input id={fid('cooldown-min-17')} type="number" className="input" style={{ width: '100%' }} value={cooldown} onChange={(e) => {
                    setCooldown(e.target.value);
                    clearRuleRequired('alert-rule-cooldown');
                  }} />
                </Field>
              </Row2>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, margin: '4px 0 10px' }}>
                <input type="checkbox" checked={autoResolve} onChange={(e) => setAutoResolve(e.target.checked)} /> Auto-resolve when the condition clears
              </label>
              <label className="field-lbl"><span>Notify channels</span></label>
              <div className="field-hint" style={{ marginBottom: 6 }}>In-app always. Add webhook/email channels for push (none = in-app only).</div>
              {channels.length === 0 ? (
                <div className="muted" style={{ fontSize: 11.5, fontStyle: 'italic' }}>No channels yet — add one in the Channels tab.</div>
              ) : (
                <MultiSelect
                  ariaLabel="Notify channels"
                  order="alpha"
                  value={channelIds}
                  options={channels.map((c) => ({ id: c.id, label: c.name, meta: c.kind }))}
                  onChange={setChannelIds}
                  placeholder="In-app only"
                  searchPlaceholder={`Search ${channels.length} channels…`}
                />
              )}
            </>
          )}
          {saveM.error && <div style={{ marginTop: 8 }}><MutationErrorLine error={saveM.error} /></div>}
        </div>

        {deleteM.error ? <div style={{ padding: '8px 16px 0' }}><MutationErrorLine error={deleteM.error} /></div> : null}
        <div className="drawer-foot" style={{ position: 'relative' }}>
          {editing && (
            <DeleteWithConfirm
              title="Delete this rule?"
              body="It stops alerting and can’t be restored."
              busy={deleteM.isPending}
              onConfirm={() => deleteM.mutate()}
            />
          )}
          {!comingSoon && missingChoice && (
            <span id={saveReasonId} className="muted" style={{ marginLeft: 'auto', fontSize: 11.5 }}>{missingChoice}</span>
          )}
          <button
            className="btn btn-primary"
            style={{ marginLeft: !comingSoon && missingChoice ? 8 : 'auto' }}
            disabled={!canBuild || saveM.isPending}
            aria-describedby={!comingSoon && missingChoice ? saveReasonId : undefined}
            onClick={attemptSaveRule}
          >
            {Icons.check} {saveM.isPending ? 'Saving…' : 'Save rule'}
          </button>
        </div>
      </ResponsiveOverlay>
    </>
  );
}
